import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useConfirm } from "@/components/ConfirmDialog";
import { Utensils, Trash2, Pencil, Users, Search, AlertTriangle } from "lucide-react";

export default function NutritionSentPage() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [q, setQ] = useState("");
    const confirm = useConfirm();

    const load = async () => {
        setLoading(true); setErr("");
        try {
            const { data } = await api.get("/meal-plans/assignments");
            setRows(data);
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const unassign = async (row) => {
        const ok = await confirm({
            title: "Remove this assignment?",
            message: `${row.client_name} will lose access to "${row.plan_name}". The meal plan template stays in your library.`,
            confirmLabel: "Remove assignment",
        });
        if (!ok) return;
        await api.delete(`/meal-plans/assignments/${row.id}`);
        setRows(rows.filter((r) => r.id !== row.id));
    };

    const filtered = rows.filter((r) =>
        !q.trim() ||
        r.client_name.toLowerCase().includes(q.toLowerCase()) ||
        r.plan_name.toLowerCase().includes(q.toLowerCase())
    );

    return (
        <div className="p-8 space-y-6" data-testid="nutrition-sent-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <p className="label-tiny mb-2">/ Nutrition · Assignments</p>
                    <h1 className="font-serif text-6xl">Sent meal plans</h1>
                    <p className="text-sm text-[var(--text-muted)] mt-2">Every active meal plan you've sent. Edit the plan or unassign here.</p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                        <input className="input-dark pl-9 w-56" placeholder="Search client or plan…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="nutrition-sent-search" />
                    </div>
                    <Link to="/meal-plans" className="btn-primary inline-flex items-center gap-2"><Utensils size={14} /> Meal plans</Link>
                </div>
            </div>

            {err && (
                <div className="border border-[var(--danger)] bg-[var(--danger)]/10 p-4 text-sm text-[var(--danger)] inline-flex items-center gap-2">
                    <AlertTriangle size={14} /> {err}
                </div>
            )}

            {!loading && filtered.length === 0 && (
                <div className="border border-dashed border-[var(--border-bright)] bg-[var(--surface)] p-12 text-center" data-testid="nutrition-sent-empty">
                    <Users size={28} className="text-[var(--gold)] mx-auto mb-4" />
                    <p className="font-serif text-3xl mb-2">No meal plans sent yet</p>
                    <p className="text-sm text-[var(--text-muted)] mb-5">Open any meal plan and tap Assign to send it to a client.</p>
                    <Link to="/meal-plans" className="btn-primary">Browse meal plans →</Link>
                </div>
            )}

            {filtered.length > 0 && (
                <div className="border border-[var(--border)] bg-[var(--surface)] overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-[var(--surface-2)]">
                            <tr className="text-left font-mono text-[10px] tracking-[0.25em] uppercase text-[var(--text-muted)]">
                                <th className="p-3">Client</th>
                                <th className="p-3">Meal plan</th>
                                <th className="p-3 text-right">Calories</th>
                                <th className="p-3 text-right">Macros</th>
                                <th className="p-3">Assigned</th>
                                <th className="p-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((r) => (
                                <tr key={r.id} className="border-t border-[var(--border)] hover:bg-[var(--surface-hover)]" data-testid={`nutrition-sent-${r.id}`}>
                                    <td className="p-3">
                                        <Link to={`/clients/${r.client_id}`} className="hover:text-[var(--gold)]">{r.client_name}</Link>
                                        {r.client_tag && <span className="ml-2 tag-pill">{r.client_tag}</span>}
                                    </td>
                                    <td className="p-3">
                                        <Link to={`/meal-plans/${r.meal_plan_id}`} className="hover:text-[var(--gold)] font-serif">{r.plan_name}</Link>
                                    </td>
                                    <td className="p-3 text-right font-display text-lg">{r.daily_calories || "—"}</td>
                                    <td className="p-3 text-right font-mono text-xs text-[var(--text-muted)]">
                                        P{r.protein_g || 0} · C{r.carbs_g || 0} · F{r.fat_g || 0}
                                    </td>
                                    <td className="p-3 font-mono text-xs text-[var(--text-muted)]">
                                        {r.assigned_at ? new Date(r.assigned_at).toLocaleDateString() : "—"}
                                    </td>
                                    <td className="p-3 text-right">
                                        <div className="inline-flex items-center gap-2">
                                            <Link to={`/meal-plans/${r.meal_plan_id}`} className="text-[var(--text-muted)] hover:text-[var(--gold)]" title="Edit plan" data-testid={`nutrition-edit-${r.id}`}>
                                                <Pencil size={14} />
                                            </Link>
                                            <button onClick={() => unassign(r)} className="text-[var(--text-muted)] hover:text-[var(--danger)]" title="Remove assignment" data-testid={`nutrition-unassign-${r.id}`}>
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
